import React, { useEffect, useMemo, useState } from 'react';
import logo from '../../assets/img/profile/logo.png';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Image,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import Content from 'components/sidebar/components/Content';
import {
  renderThumb,
  renderTrack,
  renderView,
  renderViewMini,
} from 'components/scrollbar/Scrollbar';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useJwt } from 'react-jwt';
import { getCookie } from 'cookies-next';
import { IoMenuOutline } from 'react-icons/io5';
import { MdMenu, MdMenuOpen, MdReceipt } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

function Sidebar(props: { routes: RoutesType[]; mini?: boolean; setMini?: (value: boolean) => void; [x: string]: any }) {
  const [tipoUsuario, setTipoUsuario] = useState('admin');
  const [timbres, setTimbres] = useState({ usados: 0, restantes: 0 });
  const token = getCookie('token');
  const jwt = useJwt(token);
  const user = useSelector((state: RootState) => state.auth.user);
  const { routes, mini = false, setMini } = props;

  const sidebarBg = useColorModeValue('rgba(255,255,255,.94)', 'navy.800');
  const borderColor = useColorModeValue('rgba(91, 24, 43,.16)', 'whiteAlpha.200');
  const textMuted = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    try {
      const decoded = jwt.decodedToken as any;
      if (decoded?.tipo) setTipoUsuario(decoded.tipo);
    } catch {
      setTipoUsuario('admin');
    }
    fetchTimbres();
  }, [jwt.decodedToken]);

  const fetchTimbres = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000'}/api/Facturacion/estatus-cuenta`);
      const data = await response.json();
      const detalles = data?.ConsultarCreditosResult?.Paquetes?.DetallesPaqueteCreditos;
      if (!Array.isArray(detalles)) return;

      setTimbres({
        usados: detalles.reduce((sum, item) => sum + (item.TimbresUsados || 0), 0),
        restantes: detalles.reduce((sum, item) => sum + (item.TimbresRestantes || 0), 0),
      });
    } catch {
      setTimbres({ usados: 0, restantes: 0 });
    }
  };

  const routex = useMemo(() => {
    if (tipoUsuario === 'mortal') return routes.filter((r) => r.path === '/main');
    if (tipoUsuario === 'admin') return routes;
    return routes.filter((r) => r.path === '/dashboards' || r.path === '/main');
  }, [routes, tipoUsuario]);

  return (
    tipoUsuario && (
      <Box display={{ base: 'block', sm: 'none', xl: 'block' }} position="fixed" h="100vh" p="16px" zIndex={9}>
        <Box
          bg={sidebarBg}
          backdropFilter="blur(18px)"
          transition="width .22s ease"
          width={mini ? '88px' : '296px'}
          height="calc(100vh - 32px)"
          borderRadius="28px"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="0 22px 55px rgba(67, 38, 46, 0.14)"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          position="relative"
        >
          <Box
            bgGradient="linear(160deg, #4d1223 0%, #5b182b 52%, #7a2338 100%)"
            color="white"
            p={mini ? 3 : 5}
            pb={mini ? 4 : 5}
            position="relative"
            overflow="hidden"
          >
            <Box position="absolute" right="-50px" top="-52px" w="150px" h="150px" borderRadius="full" bg="whiteAlpha.100" />
            <Box position="absolute" left="-28px" bottom="-38px" w="96px" h="96px" borderRadius="full" bg="rgba(187,140,82,0.16)" />
            <Flex align="center" justify={mini ? 'center' : 'flex-start'} position="relative" gap={3.5}>
              <Flex
                w={mini ? '58px' : '66px'}
                h={mini ? '58px' : '66px'}
                align="center"
                justify="center"
                flexShrink={0}
                borderRadius="18px"
                bg="white"
                p="3px"
                boxShadow="0 8px 22px rgba(0,0,0,0.28)"
              >
                <Image src={logo} alt="Totolac" w="100%" h="100%" objectFit="contain" />
              </Flex>
              {!mini && (
                <Box>
                  <Text fontSize="10px" letterSpacing=".18em" textTransform="uppercase" color="whiteAlpha.700" fontWeight="700">
                    Ayuntamiento de
                  </Text>
                  <Text fontSize="xl" fontWeight="900" lineHeight="1.05" letterSpacing=".01em">Totolac</Text>
                  <Text fontSize="11px" color="whiteAlpha.800">Panel administrativo</Text>
                </Box>
              )}
            </Flex>

            {!mini && (
              <Flex
                mt={4}
                align="center"
                gap={3}
                position="relative"
                bg="whiteAlpha.200"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="16px"
                px={3}
                py={2.5}
              >
                <Avatar size="sm" name={user?.name || 'Usuario'} bg="rgba(187,140,82,0.38)" color="white" />
                <Box minW={0} flex={1}>
                  <Text fontSize="sm" fontWeight="700" noOfLines={1}>{user?.name || 'Usuario'}</Text>
                  <Text fontSize="10px" color="whiteAlpha.700" noOfLines={1}>Sesion activa</Text>
                </Box>
                <Badge bg="rgba(187,140,82,0.30)" color="#f6d7aa" borderRadius="full" fontSize="10px" px={2} py={0.5}>
                  {user?.rol || tipoUsuario}
                </Badge>
              </Flex>
            )}
          </Box>

          {setMini && (
            <Box position="absolute" top="18px" right="-14px" zIndex={10}>
              <Button
                size="sm"
                bg="white"
                color="#5b182b"
                border="1px solid"
                borderColor="rgba(91, 24, 43,.18)"
                boxShadow="0 10px 22px rgba(67,38,46,.18)"
                borderRadius="full"
                w="31px"
                h="31px"
                minW="31px"
                p={0}
                onClick={() => setMini(!mini)}
                _hover={{ bg: '#f7efe8' }}
              >
                <Icon as={mini ? MdMenu : MdMenuOpen} w={4} h={4} />
              </Button>
            </Box>
          )}

          {user?.rol === 'A1' && (
            <Box px={mini ? 3 : 4} py={3} borderBottom="1px solid" borderColor={borderColor}>
              {mini ? (
                <Tooltip label={`Timbres usados: ${timbres.usados} | Restantes: ${timbres.restantes}`} placement="right">
                  <Flex justify="center">
                    <Icon as={MdReceipt} color="#5b182b" w={5} h={5} />
                  </Flex>
                </Tooltip>
              ) : (
                <HStack justify="space-between" bg="#f7efe8" borderRadius="16px" px={3} py={2}>
                  <HStack spacing={2}>
                    <Icon as={MdReceipt} color="#5b182b" />
                    <Text fontSize="xs" color={textMuted}>Timbres</Text>
                  </HStack>
                  <Text fontSize="xs" fontWeight="800" color="#5b182b">
                    {timbres.usados} / {timbres.restantes}
                  </Text>
                </HStack>
              )}
            </Box>
          )}

          <Scrollbars
            style={{ height: user?.rol === 'A1' ? 'calc(100vh - 238px)' : 'calc(100vh - 184px)' }}
            autoHide
            renderTrackVertical={renderTrack}
            renderThumbVertical={renderThumb}
            renderView={mini ? renderViewMini : renderView}
          >
            <Content mini={mini} hovered={false} routes={routex} />
          </Scrollbars>
        </Box>
      </Box>
    )
  );
}

export function SidebarResponsive(props: { routes: RoutesType[]; [x: string]: any }) {
  const sidebarBackgroundColor = useColorModeValue('white', 'navy.800');
  const menuColor = useColorModeValue('#5b182b', 'white');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef<HTMLDivElement>(null);
  const { routes } = props;

  return (
    <Flex display={{ sm: 'flex', xl: 'none' }} alignItems="center">
      <Flex ref={btnRef} w="max-content" h="max-content" onClick={onOpen}>
        <Icon as={IoMenuOutline} color={menuColor} my="auto" w="24px" h="24px" me="10px" _hover={{ cursor: 'pointer' }} />
      </Flex>
      <Drawer isOpen={isOpen} onClose={onClose} placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'} finalFocusRef={btnRef}>
        <DrawerOverlay />
        <DrawerContent w="305px" maxW="305px" m={{ sm: '16px' }} borderRadius="24px" bg={sidebarBackgroundColor} overflow="hidden">
          <DrawerCloseButton zIndex="3" onClick={onClose} _focus={{ boxShadow: 'none' }} />
          <DrawerBody maxW="305px" px="0" pb="0">
            <Scrollbars autoHide renderTrackVertical={renderTrack} renderThumbVertical={renderThumb} renderView={renderView}>
              <Content mini={false} routes={routes} />
            </Scrollbars>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default Sidebar;
